import { ExternalLink } from "lucide-react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/utils";
import { isVideoAssetUrl, toNewsAssetUrlForRender } from "@/lib/utils/apex-news";
import { newsHtmlSanitizeSchema } from "./constants";
import {
  containsMediaNode,
  extractFirstMediaSrcFromNode,
  getHeadingAnchorId,
  getYouTubeEmbedUrl,
  getYouTubeWatchUrl,
  isLikelyImagePreviewUrl,
} from "./media-utils";

export function NewsMarkdownArticle({
  normalizedBody,
  headingAnchorIdByLine,
  sourceUrl,
  title,
}: {
  normalizedBody: string;
  headingAnchorIdByLine: Map<number, string>;
  sourceUrl: string | null;
  title: string;
}) {
  return (
    <article className="space-y-4 px-1">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, newsHtmlSanitizeSchema]]}
        components={{
          a: ({ node, ...props }) => {
            const isMediaLink = containsMediaNode(node);

            if (isMediaLink) {
              const mediaSourceUrl = extractFirstMediaSrcFromNode(node);
              const previewUrl = isLikelyImagePreviewUrl(props.href)
                ? toNewsAssetUrlForRender(props.href, { referer: sourceUrl })
                : null;
              const mediaAssetUrl = toNewsAssetUrlForRender(mediaSourceUrl, {
                referer: sourceUrl,
              });
              const youtubeWatchUrl = getYouTubeWatchUrl(mediaSourceUrl);
              const youtubeEmbedUrl = getYouTubeEmbedUrl(mediaSourceUrl);

              if (youtubeWatchUrl) {
                return (
                  <span className="my-4 block space-y-2">
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt={props.title || "视频预览图"}
                        className="my-5 max-h-[440px] w-full rounded-xl border object-contain"
                        loading="lazy"
                        decoding="async"
                        referrerPolicy="no-referrer"
                      />
                    ) : youtubeEmbedUrl ? (
                      <iframe
                        src={youtubeEmbedUrl}
                        title={props.title || "YouTube 视频"}
                        className="my-5 aspect-video w-full rounded-xl border"
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                      />
                    ) : null}

                    <a
                      href={youtubeWatchUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-medium text-red-600 underline underline-offset-4 hover:text-red-500"
                    >
                      前往媒体平台观看视频
                      <ExternalLink className="size-3.5" />
                    </a>
                  </span>
                );
              }

              if (mediaSourceUrl && isVideoAssetUrl(mediaSourceUrl)) {
                return (
                  <span className="my-4 block space-y-2">
                    <video
                      controls
                      preload="metadata"
                      poster={previewUrl || undefined}
                      className="my-5 max-h-[520px] w-full rounded-xl border bg-black"
                      src={mediaAssetUrl || mediaSourceUrl}
                    />
                  </span>
                );
              }

              return (
                <span className="my-4 block space-y-2">
                  {mediaSourceUrl ? (
                    <img
                      src={mediaAssetUrl || mediaSourceUrl}
                      alt={props.title || "媒体预览图"}
                      className="my-5 max-h-[440px] w-full rounded-xl border object-contain"
                      loading="lazy"
                      decoding="async"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    props.children
                  )}
                </span>
              );
            }

            return (
              <a
                {...props}
                target={props.href?.startsWith("#") ? undefined : "_blank"}
                rel={props.href?.startsWith("#") ? undefined : "noopener noreferrer"}
                className="font-medium text-red-600 underline underline-offset-4 hover:text-red-500"
              />
            );
          },
          p: ({ node: _node, ...props }) => (
            <p {...props} className="my-3 text-base leading-7 text-foreground/95" />
          ),
          ul: ({ node: _node, ...props }) => (
            <ul {...props} className="my-3 list-disc space-y-2 pl-6" />
          ),
          ol: ({ node: _node, ...props }) => (
            <ol {...props} className="my-3 list-decimal space-y-2 pl-6" />
          ),
          h1: ({ node, ...props }) => (
            <h1
              {...props}
              id={getHeadingAnchorId(node, headingAnchorIdByLine)}
              className="mt-6 scroll-mt-24 text-3xl font-extrabold leading-tight md:scroll-mt-28"
            />
          ),
          h2: ({ node, ...props }) => (
            <h2
              {...props}
              id={getHeadingAnchorId(node, headingAnchorIdByLine)}
              className="mt-6 scroll-mt-24 text-2xl font-bold leading-tight md:scroll-mt-28"
            />
          ),
          h3: ({ node, ...props }) => (
            <h3
              {...props}
              id={getHeadingAnchorId(node, headingAnchorIdByLine)}
              className="mt-5 scroll-mt-24 text-xl font-semibold leading-tight md:scroll-mt-28"
            />
          ),
          h4: ({ node, ...props }) => (
            <h4
              {...props}
              id={getHeadingAnchorId(node, headingAnchorIdByLine)}
              className="mt-4 scroll-mt-24 text-lg font-semibold leading-tight md:scroll-mt-28"
            />
          ),
          h5: ({ node, ...props }) => (
            <h5
              {...props}
              id={getHeadingAnchorId(node, headingAnchorIdByLine)}
              className="mt-4 scroll-mt-24 text-base font-semibold leading-tight md:scroll-mt-28"
            />
          ),
          h6: ({ node, ...props }) => (
            <h6
              {...props}
              id={getHeadingAnchorId(node, headingAnchorIdByLine)}
              className="mt-4 scroll-mt-24 text-sm font-semibold leading-tight md:scroll-mt-28"
            />
          ),
          blockquote: ({ node: _node, ...props }) => (
            <blockquote
              {...props}
              className="my-4 border-l-4 border-red-500/70 py-2 pl-4 text-foreground/90"
            />
          ),
          img: ({ node: _node, ...props }) => {
            const sourceImageUrl = props.src || "";
            const youtubeEmbedUrl = getYouTubeEmbedUrl(sourceImageUrl);
            if (youtubeEmbedUrl) {
              return (
                <iframe
                  src={youtubeEmbedUrl}
                  title={props.alt || "YouTube 视频"}
                  className="my-5 aspect-video w-full rounded-xl border"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              );
            }

            const assetSrc = toNewsAssetUrlForRender(sourceImageUrl, {
              referer: sourceUrl,
            });
            if (isVideoAssetUrl(sourceImageUrl)) {
              return (
                <video
                  controls
                  preload="metadata"
                  className="my-5 max-h-[520px] w-full rounded-xl border bg-black"
                  src={assetSrc || sourceImageUrl}
                />
              );
            }
            return (
              <img
                {...props}
                src={assetSrc || sourceImageUrl}
                alt={props.alt || title}
                className="my-5 max-h-[440px] w-full rounded-xl border object-contain"
                loading="lazy"
                decoding="async"
                referrerPolicy="no-referrer"
              />
            );
          },
          hr: () => <hr className="my-6 border-border" />,
          code: (props) => <code {...props} className="rounded px-1 py-0.5 text-[0.9em]" />,
          pre: (props) => <pre {...props} className="my-4 overflow-x-auto rounded-xl border p-4" />,
          table: ({ node: _node, className, ...props }) => (
            <div className="my-5 overflow-x-auto rounded-xl border border-border/70 bg-card/40">
              <table
                {...props}
                className={cn(
                  "min-w-[560px] w-full border-collapse text-left text-sm md:text-base",
                  className,
                )}
              />
            </div>
          ),
          thead: ({ node: _node, className, ...props }) => (
            <thead {...props} className={cn("bg-muted/70", className)} />
          ),
          tbody: ({ node: _node, className, ...props }) => (
            <tbody {...props} className={cn("divide-y divide-border/70", className)} />
          ),
          tr: ({ node: _node, className, ...props }) => (
            <tr
              {...props}
              className={cn(
                "transition-colors odd:bg-background/60 even:bg-background/35 hover:bg-muted/45",
                className,
              )}
            />
          ),
          th: ({ node: _node, className, ...props }) => (
            <th
              {...props}
              className={cn(
                "border-r border-border/70 px-4 py-3 text-sm font-semibold text-foreground last:border-r-0",
                className,
              )}
            />
          ),
          td: ({ node: _node, className, ...props }) => (
            <td
              {...props}
              className={cn(
                "border-r border-border/70 px-4 py-3 align-middle text-sm text-foreground/95 last:border-r-0",
                className,
              )}
            />
          ),
        }}
      >
        {normalizedBody}
      </ReactMarkdown>
    </article>
  );
}
